import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ConsultationFilters = ({ onFilterChange, onSearch, totalCount }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedUrgency, setSelectedUrgency] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const statusOptions = [
    { value: 'all', label: 'همه وضعیت‌ها' },
    { value: 'pending', label: 'در انتظار پاسخ' },
    { value: 'in_progress', label: 'در حال بررسی' },
    { value: 'completed', label: 'تکمیل شده' },
    { value: 'cancelled', label: 'لغو شده' }
  ];

  const urgencyOptions = [
    { value: 'all', label: 'همه اولویت‌ها' },
    { value: 'high', label: 'فوری' },
    { value: 'medium', label: 'متوسط' },
    { value: 'low', label: 'عادی' }
  ];

  const categoryOptions = [
    { value: 'all', label: 'همه دسته‌ها' },
    { value: 'civil', label: 'حقوق مدنی' },
    { value: 'criminal', label: 'حقوق جزا' },
    { value: 'commercial', label: 'حقوق تجاری' },
    { value: 'family', label: 'حقوق خانواده' },
    { value: 'labor', label: 'حقوق کار' },
    { value: 'administrative', label: 'حقوق اداری' }
  ];

  const handleSearchChange = (e) => {
    const value = e?.target?.value;
    setSearchQuery(value);
    onSearch(value);
  };

  const handleFilterChange = (filterType, value) => {
    const filters = {
      status: selectedStatus,
      urgency: selectedUrgency,
      category: selectedCategory,
      [filterType]: value
    };

    switch (filterType) {
      case 'status':
        setSelectedStatus(value);
        break;
      case 'urgency':
        setSelectedUrgency(value);
        break;
      case 'category':
        setSelectedCategory(value);
        break;
    }

    onFilterChange(filters);
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedStatus('all');
    setSelectedUrgency('all');
    setSelectedCategory('all');
    setShowAdvancedFilters(false);
    onSearch('');
    onFilterChange({
      status: 'all',
      urgency: 'all',
      category: 'all'
    });
  };

  const hasActiveFilters = selectedStatus !== 'all' || selectedUrgency !== 'all' || selectedCategory !== 'all' || searchQuery;

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-reverse space-x-4">
          <h3 className="text-lg font-semibold text-card-foreground">مشاوره‌ها</h3>
          <span className="px-3 py-1 bg-muted text-muted-foreground text-sm rounded-full">
            {totalCount} مورد
          </span>
        </div>
        
        <div className="flex items-center space-x-reverse space-x-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            iconName={showAdvancedFilters ? "ChevronUp" : "ChevronDown"}
            iconPosition="left"
          >
            فیلترهای پیشرفته
          </Button>
          
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              iconName="X"
              iconPosition="right"
            >
              پاک کردن فیلترها
            </Button>
          )}
        </div>
      </div>
      {/* Search Bar */}
      <div className="mb-4">
        <Input
          type="search"
          placeholder="جستجو در مشاوره‌ها..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="w-full"
        />
      </div>
      {/* Basic Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Select
          label="وضعیت"
          options={statusOptions}
          value={selectedStatus}
          onChange={(value) => handleFilterChange('status', value)}
        />
        
        <Select
          label="اولویت"
          options={urgencyOptions}
          value={selectedUrgency}
          onChange={(value) => handleFilterChange('urgency', value)}
        />
        
        <Select
          label="دسته‌بندی"
          options={categoryOptions}
          value={selectedCategory}
          onChange={(value) => handleFilterChange('category', value)}
        />
      </div>
      {/* Advanced Filters */}
      {showAdvancedFilters && (
        <div className="border-t border-border pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="date"
              label="از تاریخ"
              className="w-full"
            />
            <Input
              type="date"
              label="تا تاریخ"
              className="w-full"
            />
          </div>
        </div>
      )}
      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex items-center space-x-reverse space-x-2 mt-4 pt-4 border-t border-border">
          <span className="text-sm text-muted-foreground">فیلترهای فعال:</span>
          
          {selectedStatus !== 'all' && (
            <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full flex items-center">
              {statusOptions?.find(opt => opt?.value === selectedStatus)?.label}
              <button
                onClick={() => handleFilterChange('status', 'all')}
                className="mr-1 hover:text-primary/70"
              >
                <Icon name="X" size={12} />
              </button>
            </span>
          )}
          
          {selectedUrgency !== 'all' && (
            <span className="px-2 py-1 bg-warning/10 text-warning text-xs rounded-full flex items-center">
              {urgencyOptions?.find(opt => opt?.value === selectedUrgency)?.label}
              <button
                onClick={() => handleFilterChange('urgency', 'all')}
                className="mr-1 hover:text-warning/70"
              >
                <Icon name="X" size={12} />
              </button>
            </span>
          )}
          
          {selectedCategory !== 'all' && (
            <span className="px-2 py-1 bg-secondary/10 text-secondary text-xs rounded-full flex items-center">
              {categoryOptions?.find(opt => opt?.value === selectedCategory)?.label}
              <button
                onClick={() => handleFilterChange('category', 'all')}
                className="mr-1 hover:text-secondary/70"
              >
                <Icon name="X" size={12} />
              </button>
            </span>
          )}
          
          {searchQuery && (
            <span className="px-2 py-1 bg-success/10 text-success text-xs rounded-full flex items-center">
              جستجو: {searchQuery}
              <button
                onClick={() => {
                  setSearchQuery('');
                  onSearch('');
                }}
                className="mr-1 hover:text-success/70"
              >
                <Icon name="X" size={12} />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ConsultationFilters;